import React, { useState } from 'react';
import {
  Phone,
  Clock,
  MapPin,
  IndianRupee,
  CalendarClock,
  Upload,
  Send,
  Loader2,
  AlertCircle,
  Video,
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { ScrollArea } from '@/Components/ui/scroll-area';

import { submitApplication } from './publicJobsApi';
import { getApplySource } from './applySource';

/**
 * ApplyDialog
 *
 * Final form before submission. Required fields:
 *   - phone
 *   - exp_yrs (total experience in years)
 *   - cur_loc (current location)
 *   - exp_ctc (expected CTC, lakhs)
 *
 * Optional:
 *   - cur_ctc, notice_period, resume (replaces the one from signup)
 *
 * On submit it POSTs to /api/public/jobs/:id/apply, then bubbles the
 * server response up to the parent so the ThankYou dialog can show
 * candidate name + job title + company.
 */
export function ApplyDialog({ job, onClose, onSuccess }) {
  const [phone, setPhone] = useState('');
  const [expYrs, setExpYrs] = useState('');
  const [curLoc, setCurLoc] = useState('');
  const [curCtc, setCurCtc] = useState('');
  const [expCtc, setExpCtc] = useState('');
  const [noticePeriod, setNoticePeriod] = useState('');
  const [resume, setResume] = useState(null);
  const [contactConsent, setContactConsent] = useState(false);
  // Talent-pool consent: unticked by default — being recommended to OTHER
  // employers must be an explicit choice, never an assumed one.
  const [talentPoolConsent, setTalentPoolConsent] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsVideoResume, setNeedsVideoResume] = useState(false);

  const validate = () => {
    if (!phone.trim() || phone.replace(/\D/g, '').length < 10) {
      return 'A valid phone number is required.';
    }
    const yrs = Number(expYrs);
    if (Number.isNaN(yrs) || yrs < 0) {
      return 'Total experience must be a non-negative number.';
    }
    if (!curLoc.trim()) {
      return 'Current location is required.';
    }
    const exp = Number(expCtc);
    if (Number.isNaN(exp) || exp <= 0) {
      return 'Expected CTC is required.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    try {
      // Which tracked link / site brought the candidate here (empty values
      // are dropped by submitApplication, so a direct visit sends nothing).
      const { source, referrer } = getApplySource(job.job_id);
      const result = await submitApplication(job.job_id, {
        source,
        referrer,
        phone: phone.trim(),
        exp_yrs: Number(expYrs),
        cur_loc: curLoc.trim(),
        cur_ctc: curCtc ? Number(curCtc) : '',
        exp_ctc: Number(expCtc),
        notice_period: noticePeriod.trim(),
        resume: resume || undefined,
        // One UI checkbox drives both channels; backend keeps them in
        // separate registries so opt-outs stay independent per channel.
        whatsapp_consent: contactConsent ? '1' : '0',
        call_consent: contactConsent ? '1' : '0',
        // Always sent ('1' or '0') so the server records an explicit choice
        // either way; an explicit choice overrides the platform default.
        talent_pool_consent: talentPoolConsent ? '1' : '0',
      });
      onSuccess?.(result);
    } catch (err) {
      // 409 means duplicate — surface a friendly message.
      if (err.status === 409) {
        setError(
          (err.body && err.body.error) ||
            'You have already applied to this job.',
        );
      } else if (err.status === 428 || err.body?.code === 'video_resume_required') {
        // This job mandates a video resume — point the candidate straight
        // at the builder instead of a dead-end error.
        setNeedsVideoResume(true);
      } else {
        setError(
          (err.body && err.body.error) ||
            err.message ||
            'Could not submit application. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // This job requires a video resume and the candidate doesn't have one —
  // swap the form for a clear call-to-action to the video resume builder.
  if (needsVideoResume) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-md p-0">
          <DialogHeader className="border-b border-border/40 px-6 py-4">
            <DialogTitle>Video resume required</DialogTitle>
            <DialogDescription>
              {job.company_name || 'This employer'} asks every applicant for a
              short video introduction before applying.
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
              <Video className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <p className="text-sm text-muted-foreground">
                It takes 2 minutes — our AI writes a personal script from your
                resume and coaches you on the recording. Once it&apos;s saved,
                come back and apply.
              </p>
            </div>
          </div>
          <DialogFooter className="border-t border-border/40 px-6 py-4">
            <Button variant="outline" onClick={() => onClose?.()}>
              Not now
            </Button>
            <Button
              onClick={() => window.open(`${API_BASE_URL}/crr`, '_blank', 'noopener')}
              className="gap-2"
            >
              <Video className="h-4 w-4" />
              Record my video resume
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose?.()}>
      <DialogContent className="max-w-lg p-0">
        <DialogHeader className="border-b border-border/40 px-6 py-4">
          <DialogTitle>Apply to {job.title}</DialogTitle>
          <DialogDescription>
            Tell us a bit more so the recruiter can review your application.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <ScrollArea className="max-h-[55vh] px-6 py-4">
            <div className="space-y-4">
              <Field
                label="Phone *"
                icon={Phone}
                value={phone}
                onChange={setPhone}
                placeholder="10-digit phone number"
                disabled={busy}
                inputMode="numeric"
              />

              <Field
                label="Total experience (years) *"
                icon={Clock}
                value={expYrs}
                onChange={setExpYrs}
                placeholder="e.g. 4"
                type="number"
                min="0"
                step="0.5"
                disabled={busy}
              />

              <Field
                label="Current location *"
                icon={MapPin}
                value={curLoc}
                onChange={setCurLoc}
                placeholder="City, State"
                disabled={busy}
              />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="Current CTC (LPA)"
                  icon={IndianRupee}
                  value={curCtc}
                  onChange={setCurCtc}
                  placeholder="6.5"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={busy}
                  hint="Optional"
                />
                <Field
                  label="Expected CTC (LPA) *"
                  icon={IndianRupee}
                  value={expCtc}
                  onChange={setExpCtc}
                  placeholder="9"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={busy}
                />
              </div>

              <Field
                label="Notice period"
                icon={CalendarClock}
                value={noticePeriod}
                onChange={setNoticePeriod}
                placeholder="30 days, immediately, etc."
                disabled={busy}
                hint="Optional"
              />

              <div className="space-y-1.5">
                <Label className="text-xs">
                  Resume (optional — replaces the one on file)
                </Label>
                <div className="relative">
                  <Upload className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                    className="pl-9"
                    disabled={busy}
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Contact consent (DPDP) — OUTSIDE the scroll area so it's always
              visible right above the buttons (a short viewport can't hide it).
              One tick records opt-in/out into BOTH whatsapp_consent and
              call_consent so each channel's opt-out stays independent. */}
          <div className="flex items-start gap-2 border-t border-border/40 px-6 py-3">
            <input
              id="contact-consent"
              type="checkbox"
              checked={contactConsent}
              onChange={(e) => setContactConsent(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-primary"
            />
            <Label
              htmlFor="contact-consent"
              className="cursor-pointer text-xs font-normal leading-snug text-muted-foreground"
            >
              I agree to receive updates and screening communication about my
              application from SearchJob.ai on WhatsApp and by phone (including
              automated AI screening calls). You can opt out anytime by replying
              STOP on WhatsApp.
            </Label>
          </div>

          {/* Talent-pool consent (DPDP) — a separate, optional choice. When
              ticked, SearchJob.ai may recommend this profile to other employers
              for roles that fit; their contact details stay hidden until an
              employer selects them. Unticked records an explicit "no". */}
          <div className="flex items-start gap-2 px-6 pb-3">
            <input
              id="talent-pool-consent"
              type="checkbox"
              checked={talentPoolConsent}
              onChange={(e) => setTalentPoolConsent(e.target.checked)}
              disabled={busy}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-input accent-primary"
            />
            <Label
              htmlFor="talent-pool-consent"
              className="cursor-pointer text-xs font-normal leading-snug text-muted-foreground"
            >
              Also consider me for other relevant roles through SearchJob.ai. My
              profile may be recommended to other employers hiring for a similar
              role; my contact details are shared only with an employer who
              selects my profile. Optional.
            </Label>
          </div>

          <DialogFooter className="border-t border-border/40 px-6 py-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Submit Application
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Tiny field wrapper to keep the form readable. All fields share the same
// icon + label + helper-text pattern; encapsulating it here removes ~80
// lines of repetition from the form body.
function Field({
  label,
  icon: Icon,
  value,
  onChange,
  placeholder,
  type = 'text',
  hint,
  ...inputProps
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center justify-between text-xs">
        <span>{label}</span>
        {hint && <span className="text-muted-foreground">{hint}</span>}
      </Label>
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9"
          {...inputProps}
        />
      </div>
    </div>
  );
}
